// PFMEA AI Assistant Page
// AI-powered analysis and reporting for Process FMEA
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import {
    Send,
    Bot,
    User,
    Trash2,
    Lightbulb,
    Copy,
    Check,
    Sparkles,
} from 'lucide-react';
import clsx from 'clsx';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppContext } from '../contexts/AppContext';
import {
    PfmeaSuggestedPrompt,
    PfmeaCategoryType,
    PFMEA_CATEGORIES,
    getPfmeaSuggestedPrompts,
} from '../constants/pfmeaSuggestedPrompts';

interface Message {
    id: string;
    role: 'user' | 'ai';
    content: string;
    timestamp: number;
}

const AIAssistantPage: React.FC = () => {
    const { theme } = useTheme();
    const { user, hasPermission } = useAuth();
    const { pfmeaItems, activeProjectName } = useAppContext();
    const isDark = theme === 'dark';

    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            role: 'ai',
            content: `Hello! I am your **PFMEA Engineering Assistant**. I have access to your Process FMEA, Control Plan, and 4M analysis data.

💡 **Select a category on the right** to see specialized prompts for:
- **Process Analysis** - Flow and structure review
- **Failure Mode Discovery** - AI-powered failure brainstorming
- **Risk Assessment** - S-O-D ratings and Action Priority
- **Control Planning** - Prevention and detection controls
- **4M Analysis** - Man, Machine, Material, Method
- **AIAG-VDA Compliance** - 7-Step methodology audit

Or type your own question below!`,
            timestamp: Date.now()
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showPrompts, setShowPrompts] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<PfmeaCategoryType>('Process Analysis');
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const suggestedPrompts = getPfmeaSuggestedPrompts();

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (promptText?: string, e?: React.FormEvent) => {
        if (e) e.preventDefault();
        const messageText = promptText || input;
        if (!messageText.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: messageText,
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            // Get auth token
            const { getAuth } = await import('firebase/auth');
            const auth = getAuth();
            const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;

            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Build context from PFMEA data
            const pfmeaContext = {
                project: activeProjectName,
                totalItems: pfmeaItems.length,
                highAP: pfmeaItems.filter(i => {
                    const ap = calculateAP(i.severity, i.occurrence, i.detection);
                    return ap === 'H';
                }).length,
                processSteps: [...new Set(pfmeaItems.map(i => i.processStepName))],
            };

            // Call backend for AI response (use full URL for production)
            const backendUrl = import.meta.env.VITE_BACKEND_URL || '';
            const response = await fetch(`${backendUrl}/api/agents/workflow/coach`, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    prompt: `You are an expert PFMEA (Process Failure Mode and Effects Analysis) engineer.

## CONTEXT
Project: ${pfmeaContext.project || 'Current Project'}
Total PFMEA Items: ${pfmeaContext.totalItems}
High Action Priority Items: ${pfmeaContext.highAP}
Process Steps: ${pfmeaContext.processSteps.slice(0, 10).join(', ')}

## USER QUESTION
${messageText}

## INSTRUCTIONS
Provide a detailed, actionable response following AIAG-VDA 2019 PFMEA methodology.
Use markdown formatting with headers, bullet points, and tables where appropriate.
If recommending actions, be specific about who should do what and by when.
Reference specific S-O-D ratings and Action Priority levels when relevant.`
                }),
            });

            let aiContent: string;

            if (response.ok) {
                const result = await response.json();
                aiContent = typeof result === 'string'
                    ? result
                    : result.response || result.content || JSON.stringify(result, null, 2);
            } else {
                aiContent = `I apologize, but I couldn't connect to the AI service. 

**Common troubleshooting steps:**
- Ensure the Magnus backend is running
- Check your network connection
- Verify you have the required permissions

Error code: ${response.status}`;
            }

            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: aiContent,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            console.error('AI Assistant error:', error);
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: 'I encountered an error processing your request. Please try again.',
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePromptClick = (prompt: PfmeaSuggestedPrompt) => {
        handleSend(prompt.prompt);
    };

    const clearChat = () => {
        setMessages([
            {
                id: Date.now().toString(),
                role: 'ai',
                content: 'Chat cleared! Select a category on the right or ask me anything about your Process FMEA.',
                timestamp: Date.now()
            }
        ]);
    };

    const copyToClipboard = async (content: string, id: string) => {
        await navigator.clipboard.writeText(content);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const calculateAP = (s: number, o: number, d: number): 'H' | 'M' | 'L' => {
        if (s >= 9 || (s >= 8 && o >= 4)) return 'H';
        if (s >= 7 && o >= 4 && d >= 4) return 'H';
        if (s >= 7 || (s >= 5 && o >= 4)) return 'M';
        return 'L';
    };

    const filteredPrompts = suggestedPrompts.filter(p => p.category === selectedCategory);
    const currentCategory = PFMEA_CATEGORIES.find(c => c.id === selectedCategory);

    if (!hasPermission('ai.generate_pfmea')) {
        return (
            <div className={clsx(
                'min-h-[60vh] flex items-center justify-center',
                isDark ? 'bg-slate-900' : 'bg-slate-50'
            )}>
                <div className="text-center">
                    <Sparkles className="mx-auto text-slate-400 mb-4" size={48} />
                    <h2 className={clsx('text-xl font-semibold mb-2', isDark ? 'text-white' : 'text-slate-800')}>
                        AI Assistant Not Available
                    </h2>
                    <p className="text-slate-500">
                        You need Engineer or higher role to use AI features.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex gap-4 h-[calc(100vh-8rem)]">
            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h1 className={clsx(
                            'text-2xl font-bold flex items-center gap-2',
                            isDark ? 'text-white' : 'text-slate-800'
                        )}>
                            <Bot className="text-purple-500" />
                            AI Assistant
                        </h1>
                        <p className={clsx('text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
                            Ask questions about Process FMEA, failure modes, and control plans
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowPrompts(!showPrompts)}
                            className={clsx(
                                'flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors',
                                isDark
                                    ? 'border-purple-700 text-purple-400 hover:bg-purple-900/30'
                                    : 'border-purple-200 text-purple-600 hover:bg-purple-50'
                            )}
                        >
                            <Lightbulb size={16} />
                            {showPrompts ? 'Hide' : 'Show'} Prompts
                        </button>
                        <button
                            onClick={clearChat}
                            className={clsx(
                                'flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border transition-colors',
                                isDark
                                    ? 'border-red-800 text-red-400 hover:bg-red-900/30'
                                    : 'border-red-200 text-red-500 hover:bg-red-50'
                            )}
                        >
                            <Trash2 size={16} />
                            Clear
                        </button>
                    </div>
                </div>

                {/* Chat Container */}
                <div className={clsx(
                    'flex-1 rounded-xl border overflow-hidden flex flex-col',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
                )}>
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-4">
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={clsx(
                                    'flex',
                                    msg.role === 'user' ? 'justify-end' : 'justify-start'
                                )}
                            >
                                {msg.role === 'user' ? (
                                    <div className="max-w-[80%] rounded-xl p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-br-sm">
                                        <div className="flex items-center gap-2 mb-2 opacity-70 text-xs">
                                            <User size={12} />
                                            <span>{user?.email?.split('@')[0] || 'You'}</span>
                                        </div>
                                        <div className="whitespace-pre-wrap text-sm">
                                            {msg.content}
                                        </div>
                                    </div>
                                ) : (
                                    <div className={clsx(
                                        'max-w-[95%] w-full rounded-xl p-4 rounded-bl-sm',
                                        isDark ? 'bg-slate-700' : 'bg-slate-50 border border-slate-200'
                                    )}>
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="flex items-center gap-2 text-xs">
                                                <Bot size={14} className="text-purple-500" />
                                                <span className={clsx(
                                                    'font-medium',
                                                    isDark ? 'text-slate-300' : 'text-slate-600'
                                                )}>
                                                    PFMEA Assistant
                                                </span>
                                                <span className={clsx(
                                                    'opacity-60',
                                                    isDark ? 'text-slate-400' : 'text-slate-500'
                                                )}>
                                                    {new Date(msg.timestamp).toLocaleTimeString()}
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => copyToClipboard(msg.content, msg.id)}
                                                className={clsx(
                                                    'p-1 rounded transition-colors',
                                                    isDark ? 'hover:bg-slate-600' : 'hover:bg-slate-200'
                                                )}
                                                title="Copy to clipboard"
                                            >
                                                {copiedId === msg.id ? (
                                                    <Check size={14} className="text-green-500" />
                                                ) : (
                                                    <Copy size={14} className={isDark ? 'text-slate-400' : 'text-slate-500'} />
                                                )}
                                            </button>
                                        </div>
                                        <div className={clsx(
                                            'prose prose-sm max-w-none',
                                            isDark ? 'prose-invert' : ''
                                        )}>
                                            <div className="text-sm whitespace-pre-wrap">
                                                {msg.content.split(/(\*\*.*?\*\*|\n)/g).map((part, i) => {
                                                    if (part.startsWith('**') && part.endsWith('**')) {
                                                        return <strong key={i}>{part.slice(2, -2)}</strong>;
                                                    }
                                                    if (part === '\n') {
                                                        return <br key={i} />;
                                                    }
                                                    return <span key={i}>{part}</span>;
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {isLoading && (
                            <div className="flex justify-start">
                                <div className={clsx(
                                    'rounded-xl p-4 rounded-bl-sm',
                                    isDark ? 'bg-slate-700' : 'bg-slate-50 border border-slate-200'
                                )}>
                                    <div className="flex gap-1">
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className={clsx(
                        'p-4 border-t',
                        isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50/50'
                    )}>
                        <form onSubmit={(e) => handleSend(undefined, e)} className="flex gap-2">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about failure modes, risks, or DVP&R..."
                                className={clsx(
                                    'flex-1 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500',
                                    isDark
                                        ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                                        : 'bg-white border-slate-300 text-slate-800 placeholder-slate-400'
                                )}
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                disabled={isLoading || !input.trim()}
                                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                            >
                                <Send size={20} />
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Suggested Prompts Sidebar */}
            {showPrompts && (
                <div className={clsx(
                    'w-96 flex flex-col rounded-xl border p-4',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
                )}>
                    {/* Header */}
                    <div className="mb-3">
                        <h2 className={clsx(
                            'text-lg font-bold flex items-center gap-2',
                            isDark ? 'text-white' : 'text-slate-800'
                        )}>
                            <Lightbulb className="text-yellow-500" />
                            Suggested Prompts
                        </h2>
                        <p className={clsx('text-xs mt-1', isDark ? 'text-slate-400' : 'text-slate-500')}>
                            Select a category, then click any prompt
                        </p>
                    </div>

                    {/* Category Selection */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                        {PFMEA_CATEGORIES.map((cat) => (
                            <button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                className={clsx(
                                    'flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left',
                                    selectedCategory === cat.id
                                        ? cat.activeColor + ' shadow-md border-transparent'
                                        : isDark
                                            ? 'bg-slate-700 border-slate-600 text-slate-300 hover:border-purple-500/50'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-purple-300'
                                )}
                            >
                                <div className={selectedCategory === cat.id ? 'text-white' : ''}>
                                    {cat.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-xs truncate">{cat.id}</div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Category Description */}
                    <div className={clsx(
                        'p-3 rounded-lg mb-3 border',
                        isDark
                            ? 'bg-slate-700 border-slate-600'
                            : currentCategory?.color
                    )}>
                        <div className="flex items-center gap-2 mb-1">
                            {currentCategory?.icon}
                            <span className={clsx(
                                'font-bold text-sm',
                                isDark ? 'text-white' : ''
                            )}>
                                {selectedCategory}
                            </span>
                        </div>
                        <p className={clsx(
                            'text-xs',
                            isDark ? 'text-slate-300' : ''
                        )}>
                            {currentCategory?.description}
                        </p>
                        <div className={clsx(
                            'text-xs mt-2 font-semibold',
                            isDark ? 'text-slate-400' : ''
                        )}>
                            {filteredPrompts.length} prompts available
                        </div>
                    </div>

                    {/* Filtered Prompts */}
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {filteredPrompts.map((prompt, index) => (
                            <button
                                key={prompt.id}
                                onClick={() => handlePromptClick(prompt)}
                                disabled={isLoading}
                                className={clsx(
                                    'w-full text-left rounded-lg p-3 border transition-all disabled:opacity-50 disabled:cursor-not-allowed group',
                                    isDark
                                        ? 'bg-slate-700 border-slate-600 hover:border-purple-500 hover:shadow-lg'
                                        : 'bg-white border-slate-200 hover:border-purple-400 hover:shadow-md'
                                )}
                            >
                                <div className="flex items-start gap-2 mb-1.5">
                                    <div className={clsx(
                                        'mt-0.5',
                                        isDark ? 'text-purple-400' : 'text-purple-600'
                                    )}>
                                        {prompt.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className={clsx(
                                                'w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0',
                                                isDark
                                                    ? 'bg-purple-900/50 text-purple-300'
                                                    : 'bg-purple-100 text-purple-600'
                                            )}>
                                                {index + 1}
                                            </span>
                                            <div className={clsx(
                                                'font-semibold text-xs truncate group-hover:text-purple-500 transition-colors',
                                                isDark ? 'text-white' : 'text-slate-800'
                                            )}>
                                                {prompt.title}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <p className={clsx(
                                    'text-xs line-clamp-2 ml-7',
                                    isDark ? 'text-slate-400' : 'text-slate-500'
                                )}>
                                    {prompt.description}
                                </p>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AIAssistantPage;
